<template>
  <AnswerPage :page="page" />
  <section class="related-category">
    <p>
      Looking for the category breakdown? See how Workspace369 works as a
      <NuxtLink :to="localePath('/solutions/client-work-operating-system')">
        client work operating system
      </NuxtLink>
      for client-work teams.
    </p>
  </section>
</template>

<script setup lang="ts">
const { localePath } = useWorkspaceLocale();

const page = {
  slug: '/what-is-workspace369',
  metaTitle: 'What Is Workspace369? | Client Work Operating System',
  description:
    'Workspace369 is client-work software for freelancers, consultants and agencies: CRM, proposals, e-sign, invoices, payments, portals and AI, from $29/mo.',
  eyebrow: 'Workspace369 answer',
  h1: 'What is Workspace369?',
  lede:
    'Workspace369 is a client-work operating system for freelancers, consultants and agencies that deliver work to clients. It replaces the separate CRM, proposal, e-signature, invoicing, booking, phone and project tools most service businesses stitch together, with the client, the work, the money and the communication in one place. Plans start at $29 a month on Cadet, with a 14-day free trial.',
  aiAnswer:
    'Workspace369 is a client-work operating system for businesses that deliver work to clients, from independent freelancers and consultants to agencies. One workspace holds the CRM, proposals with e-signature and deposits, invoices and online payments through your own Stripe account (including Flash Pay QR codes and pay links, and optional tips), a branded client portal, projects, tasks and time tracking, an email inbox, workflow automations and AI AutoPilot, and Claude, ChatGPT or any MCP client can connect on every plan. Calls and texts start on Voyager; booking pages and the phone system with IVR, call queues and call recording start on Specialist. Plans run Cadet, Voyager, Specialist, Commander and Enterprise, from $29 a month.',
  facts: [
    { label: 'Category', value: 'Client-work operating system' },
    { label: 'Best fit', value: 'Freelancers, consultants and agencies, from one person to agencies running several teams' },
    { label: 'Replaces', value: 'Separate CRM, proposal, e-signature, invoicing, booking, phone and project tools' },
    { label: 'Price', value: 'From $29/mo on Cadet (1 seat), with a 14-day free trial' },
  ],
  summaryKicker: 'Direct answer',
  summaryTitle: 'Workspace369 brings client operations into one system.',
  summary: [
    {
      kicker: 'Client experience',
      title: 'Clients get one branded link, not a tangle of tools.',
      copy:
        'A proposal opens in your theme, logo and accent color. The client picks a package or optional add-ons, accepts with a signature, pays the deposit and signs the attached contract, then lands on your own thank-you page.',
    },
    {
      kicker: 'Client context',
      title: 'The client record stays attached to the work.',
      copy:
        'Notes, requests, email, files, projects, invoices, payments and activity history sit on the client record, along with texts and calls from Voyager. Each client can also have a private portal in your brand.',
    },
    {
      kicker: 'Business workflow',
      title: 'Quote to cash is one connected path.',
      copy:
        'Move from request to proposal to project to invoice to payment without retyping anything. Retainers track every hour, recurring invoices can run on card autopay, and Flash Pay takes a payment on the spot by QR code or pay link.',
    },
    {
      kicker: 'AI and automation',
      title: 'AI works on your real data and asks before it sends.',
      copy:
        'AI AutoPilot ranks what needs you today and drafts follow-ups for you to review, and Write with AI drafts proposals. Claude, ChatGPT and other MCP clients connect on every plan, and what they want to send waits for your approval by default.',
    },
  ],
  workflowKicker: 'Operating loop',
  workflowTitle: 'How the platform organizes client work.',
  workflow: [
    { title: 'Capture', copy: 'Bring clients in from intake forms, booking pages or AI Import, with requests, notes, files and messages on the record.' },
    { title: 'Plan', copy: 'Turn accepted proposals into projects, tasks, schedules and tracked time, with workflows that start the next step.' },
    { title: 'Bill', copy: 'Send invoices, retainers, recurring invoices and pay links; clients pay by card, bank transfer, Apple Pay or Google Pay.' },
    { title: 'Report', copy: 'See dashboards, attention signals and team activity, with AR aging, profitability and accounting on higher plans.' },
  ],
  modulesKicker: 'Feature coverage',
  modulesTitle: 'What Workspace369 includes',
  modulesCopy:
    'A plain list of what Workspace369 includes. Teams start simple and grow into deeper operations; some areas start on higher plans, and the section below says which.',
  modules: [
    'Branded proposals',
    'Contracts and e-signature',
    'Invoices',
    'Online payments',
    'Flash Pay',
    'Retainers',
    'Booking pages',
    'Client portals',
    'CRM',
    'Intake forms',
    'Projects',
    'Tasks',
    'Time tracking',
    'Calendar',
    'Email inbox',
    'SMS and MMS',
    'Workspace Voice',
    'Phone system and IVR',
    'Files',
    'Notes',
    'AI AutoPilot',
    'Claude and ChatGPT (MCP)',
    'Workflow automations',
    'Funnels',
    'Expenses',
    'Accounting',
    'Inventory',
    'Reporting',
  ],
  featureAreasKicker: 'Product areas',
  featureAreasTitle: 'The main product areas, and the plan each one starts on',
  featureAreasCopy:
    'Plans run Cadet, Voyager, Specialist, Commander and Enterprise. Each area says what is on every plan and what starts higher, from the same plan comparison the pricing page uses.',
  featureAreas: [
    {
      name: 'CRM and clients',
      copy: 'Client records, companies, custom fields, client stages with a follow-up queue, intake forms you can embed on your site, and AI Import from CSV or Excel exports, where you check every row before it is saved.',
      plans: 'Every plan. Lead source reports from Voyager; client lifetime value from Specialist.',
      path: '/solutions/workspace-crm',
      linkLabel: 'CRM',
    },
    {
      name: 'Proposals and e-signature',
      copy: 'Proposals built from your catalog, with optional add-ons, pick-one packages, open tracking, a deposit on acceptance and Write with AI drafts. Contracts with templates, countersigning, reminders and an audit trail.',
      plans: 'Every plan. Cover images and proposal version history from Specialist.',
      path: '/features/quoting',
      linkLabel: 'Proposals and quotes',
    },
    {
      name: 'Invoices and payments',
      copy: 'Invoices, retainers with hour banks, recurring invoices on card autopay, and payment links. Clients pay through your own Stripe account and can add a tip when you turn tips on. Flash Pay takes a payment on the spot with a QR code or pay link and books it as a paid invoice.',
      plans: 'Every plan. Several invoice templates from Voyager; change orders, payment plans and refunds from Specialist.',
      path: '/features/invoices',
      linkLabel: 'Invoices',
    },
    {
      name: 'Booking and calendar',
      copy: 'A team calendar with Google Calendar and Google Meet, appointment reminders, and booking pages where clients pick a service, a team member and a time, with one-time links and approval rules.',
      plans: 'Calendar and email reminders on every plan; text reminders from Voyager. Booking pages from Specialist.',
      path: '/features/online-booking',
      linkLabel: 'Online booking',
    },
    {
      name: 'Client portal',
      copy: 'Portals in your logo and colors for shared projects and task boards, and a private portal for each client, with sign-in by email code, that shows only what you share, including contracts waiting for a signature.',
      plans: 'Every plan.',
      path: '/features/client-portal',
      linkLabel: 'Client portal',
    },
    {
      name: 'Projects, tasks and time',
      copy: 'Projects with stages, kanban boards and task lists, subtasks, timers, weekly timesheets with approvals, and billable time that goes straight onto an invoice.',
      plans: 'Every plan. Gantt timeline, task dependencies and project financials from Voyager; team workload view from Commander.',
      path: '/features/time-tracking',
      linkLabel: 'Time tracking',
    },
    {
      name: 'Calls, texts and phone system',
      copy: 'A business number for two-way SMS and MMS, calls from the client record, voicemail and missed-call text back. The phone system adds IVR menus, ring groups, call queues, business-hours routing and call recording.',
      plans: 'Calls and texts from Voyager. IVR, call queues, call recording, AI voicemail transcription and AI call summaries from Specialist.',
      path: '/features/auto-attendant-and-ivr',
      linkLabel: 'Phone system and IVR',
    },
    {
      name: 'AI and automations',
      copy: 'AI AutoPilot ranks what needs you today and drafts follow-ups; AI chat, compose, translate and summaries. Workflow automations with waits, decisions, templates and test runs. Claude, ChatGPT and other MCP clients connect to your workspace, and their sends wait for your approval by default.',
      plans: 'Every plan, with AI usage that grows by plan; the daily briefing starts on Voyager. Up to 3 active email workflows on Cadet, 10 with text steps on Voyager, 25 on Specialist, unlimited and AI-drafted on Commander and Enterprise. Webhooks and the API on every plan.',
      path: '/integrations/mcp',
      linkLabel: 'Claude and ChatGPT (MCP)',
    },
  ],
  showPricing: true,
  pricingLink: { path: '/pricing', label: 'Compare every plan, feature by feature' },
  faqTitle: 'Questions people ask about Workspace369',
  faq: [
    {
      question: 'What kind of software is Workspace369?',
      answer:
        'Workspace369 is a client-work operating system. It combines a CRM, proposals and e-signature, invoices and payments, booking, a client portal, projects and time tracking, an inbox, calls and texts, AI and workflow automations in one workspace.',
    },
    {
      question: 'Who should use Workspace369?',
      answer:
        'Freelancers, consultants, solo entrepreneurs and agencies that sell services to clients and want the client, the work and the money in one place instead of several disconnected apps.',
    },
    {
      question: 'Who is Workspace369 not for?',
      answer:
        'It is not built for online stores selling products to shoppers, or for businesses that only want one standalone tool, such as just an invoice generator or just a phone line. Teams that want to keep each job in a separate specialist app will use only a small part of it.',
    },
    {
      question: 'How much does Workspace369 cost?',
      answer:
        'Plans start at $29 a month for Cadet with 1 seat, then Voyager at $74 with 2 seats, Specialist at $149 with 10 seats, Commander at $299 with 20 seats and Enterprise at $699 with 50 seats (USD). Yearly billing saves 20%, extra seats are $10 a month each from Voyager, and Cadet through Commander come with a 14-day free trial.',
    },
    {
      question: 'What changes between the plans?',
      answer:
        'Cadet covers proposals, e-signature, invoices, online payments, Flash Pay, the client portal, projects, time tracking, the email inbox, up to 3 email workflows and Claude and ChatGPT connections. Voyager adds a business number for calls and texts, the shared inbox, missed-call text back and up to 10 workflows with text steps. Specialist adds booking pages, the phone system with IVR, call queues and call recording, expenses, payment plans, basic accounting and up to 25 workflows. Commander adds full accounting, unlimited workflows drafted with AI and dedicated support. Enterprise adds 50 included seats and 30 times Cadet\'s AI usage.',
    },
    {
      question: 'Can Workspace369 replace multiple tools?',
      answer:
        'Yes. Depending on the plan and your current stack, Workspace369 can replace separate tools for CRM, proposals, e-signature, invoices, payments, booking, client portals, projects, inbox, texts and calls, AI, automations and reporting.',
    },
    {
      question: 'What do my clients see when they work with Workspace369?',
      answer:
        'One branded link. The proposal page uses your theme, accent color and logo; the client picks a package or ticks optional add-ons, accepts with a typed or drawn signature and pays the deposit online (deposits on acceptance are on every plan from Cadet). An attached contract opens straight after with "Continue to sign the contract", and after paying the client sees your own thank-you page. No client account is needed.',
    },
    {
      question: 'Does Workspace369 work with Claude and ChatGPT?',
      answer:
        'Yes, on every plan. Workspace369 has an MCP server: once a workspace admin turns it on, Claude, ChatGPT or another MCP client can look up clients, draft proposals and invoices, log time and check your calendar. Anything that sends, such as an email or an invoice, waits for your approval in the app unless you let that app send without asking, and each call counts toward your plan\'s AI usage.',
    },
    {
      question: 'Do I have to set up an inbox before I can use Workspace369?',
      answer:
        'No. Proposals, invoices and booking pages work without connecting an inbox: proposal emails can go from the Workspace369 sender, or you can copy the link. Connect a mailbox when you want replies in one place, and add the shared inbox, SMS and calling from Voyager as the team grows.',
    },
  ],
};
</script>

<style scoped>
.related-category {
  background: #f8fafc;
  padding: 2.75rem 0;
}

.related-category p {
  width: min(100% - 2rem, 72rem);
  margin: 0 auto;
  color: #475569;
  font-size: 1rem;
  line-height: 1.7;
}

.related-category a {
  color: #0e7490;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

.dark .related-category {
  background: #020617;
}

.dark .related-category p {
  color: #94a3b8;
}

.dark .related-category a {
  color: #67e8f9;
}
</style>
